import React, { Fragment, useEffect, useRef } from "react";
import { conversation, logParams, message } from "../types";

export const Log = ({ conv, readonly, setStage }: logParams) => {
    const logRef = useRef(null);

    useEffect(() => {
        (logRef as any).current.scrollTop = 9999999999;
    }, [conv.messages]);

    return (
        <div className="log" ref={logRef}>
            {conv.messages
                ? conv.messages.map((message, index) => {
                      const sideClass = message.incoming ? "line userline" : "line botline";
                      if (message.contenttype === "text") {
                          return (
                              <Fragment key={message.muid}>
                                  {Timestamp(message, index, conv.messages)}
                                  <div className={sideClass} dangerouslySetInnerHTML={{ __html: message.msgtext ? message.msgtext : "" }} />
                                  {index !== conv.messages.length - 1 ? null : Suggest(message, readonly, setStage)}
                              </Fragment>
                          );
                      } else if (message.contenttype === "image") {
                          return (
                              <Fragment key={message.muid}>
                                  {Timestamp(message, index, conv.messages)}
                                  <div className="line botline media-bubble">
                                      <img alt="" src={message.msgimage} style={{ borderRadius: "18px", width: "100%" }} />
                                  </div>
                              </Fragment>
                          );
                      } else if (message.contenttype === "template") {
                          return (
                              <Fragment key={message.muid}>
                                  {Timestamp(message, index, conv.messages)}
                                  <div>{Template(message, readonly, setStage)}</div>
                              </Fragment>
                          );
                      } else if (message.contenttype === "sound") {
                          return (
                              <Fragment key={message.muid}>
                                  {Timestamp(message, index, conv.messages)}
                                  <div className="line botline media-bubble">
                                      <audio controls src={message.msgaudio} style={{ borderRadius: "18px", width: "100%" }} />
                                  </div>
                              </Fragment>
                          );
                      } else if (message.contenttype === "video") {
                          return (
                              <Fragment key={message.muid}>
                                  {Timestamp(message, index, conv.messages)}
                                  <Video {...message} />
                              </Fragment>
                          );
                      } else if (message.contenttype === "no-response" || message.contenttype === "end") {
                          return null;
                      } else if (message.contenttype === "typing") {
                          if (index === conv.messages.length - 1) {
                              return (
                                  <div key={message.muid} className={sideClass}>
                                      <img alt="" src="dots.gif" />
                                  </div>
                              );
                          }
                          return null;
                      } else if (message.contenttype === "transfer") {
                          const parser = new DOMParser();
                          const actions = parser.parseFromString(message.msgactions ? message.msgactions : "<actions />", "text/xml");
                          const transferResult = actions.evaluate("//actions/transfer", actions, null, 0, null);
                          const transferDOM = transferResult.iterateNext();
                          let tobot = false;
                          let toqueue = false;
                          if (transferDOM) tobot = actions.evaluate("@tobot", transferDOM, null, XPathResult.STRING_TYPE, null).stringValue === "1";
                          if (transferDOM) toqueue = actions.evaluate("@toqueue", transferDOM, null, XPathResult.STRING_TYPE, null).stringValue === "1";
                          return (
                              <Fragment key={message.muid}>
                                  {Timestamp(message, index, conv.messages)}
                                  <div className="divider">
                                      <div className="dividerline" />
                                      <span className="dividertext">{"Transfer to " + (toqueue ? "queue" : tobot ? "bot" : "agent")}</span>
                                      <div className="dividerline" />
                                  </div>
                              </Fragment>
                          );
                      }
                      return (
                          <div key={message.muid} className={sideClass}>
                              [{message.contenttype}]
                          </div>
                      );
                  })
                : null}
        </div>
    );
};

function showDate(thisdate: string, prevdate: string): string {
    const localzsuffix = thisdate.endsWith("Z") ? "" : "Z";
    const prevzsuffix = prevdate.endsWith("Z") ? "" : "Z";
    const localdate = new Date(thisdate + localzsuffix);
    const localtimestamp = localdate.getTime();
    const prev = new Date(prevdate + prevzsuffix).getTime();
    const diffMS = localtimestamp - prev;
    const diffHours = diffMS / (1000 * 60 * 24);
    if (thisdate === prevdate || diffHours > 24) {
        return localdate.toLocaleDateString() + " " + localdate.toLocaleTimeString();
    }
    return "";
}

function Timestamp(message: message, index: number, messages: message[]) {
    const prevMessages = messages.filter((msg, i) => i < index && msg.contenttype !== "typing" && msg.contenttype !== "no-response");
    const prevMessageCreated = index > 0 && prevMessages.length ? prevMessages[prevMessages.length - 1].created : message.created;
    const dateText = showDate(message.created, index === 0 ? message.created : prevMessageCreated);
    return dateText ? (
        <div key={message.muid} className="divider">
            <div className="dividerpad" />
            <span className="dividertext">{dateText}</span>
            <div className="dividerpad" />
        </div>
    ) : null;
}

function Video(message: message) {
    const parser = new DOMParser();
    const actions = parser.parseFromString(message.msgactions ? message.msgactions : "", "text/xml");
    const videoType = actions.getElementsByTagName("video")[0].getAttribute("type");
    const videoID = actions.getElementsByTagName("video")[0].getAttribute("id");
    if (videoType == null || (videoType != null && videoType === "impersonmedia")) {
        const reply = message.msgvideo;
        return (
            <div key={message.muid} className="line botline media-bubble">
                <video controls src={reply} style={{ borderRadius: "18px", width: "100%" }} />
            </div>
        );
    } else if (videoType != null && videoType === "youtube") {
        return (
            <div key={message.muid} className="line botline media-bubble">
                <iframe title={videoID ? `YouTube video ${videoID}` : "YouTube video"} src={"https://www.youtube.com/embed/" + videoID + "?autoplay=1"} frameBorder={0} />
            </div>
        );
    }
    return null;
}

function Suggest(message: message, readonly: boolean, setStage: any) {
    if (message.msgactions) {
        const parser = new DOMParser();
        const actions = parser.parseFromString(message.msgactions, "text/xml");
        const suggestResult = actions.evaluate("//actions/suggest", actions, null, 0, null);
        const suggestOptionsDOM = suggestResult.iterateNext();
        if (suggestOptionsDOM) {
            const suggestOptionsText = suggestOptionsDOM.textContent;
            const suggestOptions = suggestOptionsText ? suggestOptionsText.split(",") : [];
            return (
                <div className="line suggestline">
                    {suggestOptions.map(btn => {
                        const txt = btn.trim();
                        return (
                            <button
                                key={txt}
                                className="suggestbutton"
                                type="button"
                                style={{ marginLeft: "5px" }}
                                onClick={() => {
                                    if (!readonly) {
                                        setStage({ stage: "post", value: txt, value2: txt });
                                    }
                                    return false;
                                }}>
                                {txt}
                            </button>
                        );
                    })}
                </div>
            );
        }
        return null;
    }
    return null;
}

const getAction = (buttonType: string, buttonUrl: string, buttonPayload: string, buttonTitle: string, readonly: boolean, setStage: any): any => {
    return readonly
        ? () => {
              return false;
          }
        : buttonType === "web_url"
        ? () => {
              window.open(buttonUrl, "_blank");
              return false;
          }
        : buttonType === "postback"
        ? () => {
              setStage({ stage: "post", value: buttonPayload, value2: buttonTitle });
              return false;
          }
        : () => {
              return false;
          };
};

const getButtons = (buttonsResult: XPathResult, doc: Document, readonly: boolean, setStage: any) => {
    const buttons = [];
    let button = buttonsResult.iterateNext();
    while (button) {
        const buttonType = doc.evaluate("@type", button, null, XPathResult.STRING_TYPE, null).stringValue;
        const buttonTitle = doc.evaluate("@title", button, null, XPathResult.STRING_TYPE, null).stringValue;
        const buttonUrl = doc.evaluate("@url", button, null, XPathResult.STRING_TYPE, null).stringValue;
        const buttonPayload = doc.evaluate("@payload", button, null, XPathResult.STRING_TYPE, null).stringValue;
        buttons.push(
            <tr key={`${buttonType}-${buttonTitle}-${buttonPayload}`}>
                <td>
                    <button
                        type="button"
                        className="templatebutton"
                        style={{ width: "100%" }}
                        onClick={getAction(buttonType, buttonUrl, buttonPayload, buttonTitle, readonly, setStage)}>
                        {buttonTitle}
                    </button>
                </td>
            </tr>
        );
        button = buttonsResult.iterateNext();
    }
    return (
        <table style={{ width: "100%", borderSpacing: "0px", borderCollapse: "collapse" }}>
            <tbody>{buttons}</tbody>
        </table>
    );
};

function getElements(elementsResult: XPathResult, doc: Document, readonly: boolean, setStage: any) {
    let numberOfElements = 0;
    const imageRow = [];
    const titleRow = [];
    const buttonsRow = [];
    let element = elementsResult.iterateNext();
    while (element) {
        numberOfElements++;
        const elementTitle = doc.evaluate("@title", element, null, XPathResult.STRING_TYPE, null).stringValue;
        const elementSubTitle = doc.evaluate("@subtitle", element, null, XPathResult.STRING_TYPE, null).stringValue;
        const elementURL = doc.evaluate("@image_url", element, null, XPathResult.STRING_TYPE, null).stringValue;
        const elementButtonsHTML = getButtons(doc.evaluate("button", element, null, XPathResult.ANY_TYPE, null), doc, readonly, setStage);
        imageRow.push(
            <td key={`${elementTitle}-image`}>
                <img alt="" src={elementURL} className="templateimage" />
            </td>
        );
        titleRow.push(
            <td key={`${elementTitle}-title`}>
                <div className="templatetitle">{elementTitle}</div>
                <div className="templatesubtitle">{elementSubTitle}</div>
            </td>
        );
        buttonsRow.push(
            <td key={`${elementTitle}-buttons`} style={{ backgroundColor: "white", verticalAlign: "bottom" }}>
                {elementButtonsHTML}
            </td>
        );
        element = elementsResult.iterateNext();
    }
    const widthText = "calc(80% * " + numberOfElements.toString() + ")";
    return (
        <table
            style={{
                tableLayout: "fixed",
                backgroundColor: "transparent",
                width: widthText
            }}>
            <tbody>
                <tr>{imageRow}</tr>
                <tr>{titleRow}</tr>
                <tr>{buttonsRow}</tr>
            </tbody>
        </table>
    );
}

function Template(message: message, readonly: boolean, setStage: any) {
    if (message.msgactions) {
        const parser = new DOMParser();
        const actions = parser.parseFromString(message.msgactions, "text/xml");
        const templateType = actions.evaluate("//actions/template/@type", actions, null, XPathResult.STRING_TYPE, null).stringValue;
        switch (templateType) {
            case "button": {
                const templateText = actions.evaluate("//actions/template/@text", actions, null, XPathResult.STRING_TYPE, null).stringValue;
                const templateButtons = actions.evaluate("//actions/template/button", actions, null, XPathResult.ANY_TYPE, null);
                return (
                    <Fragment>
                        <div className="line botline">{templateText}</div>
                        <div className="line templateline">{getButtons(templateButtons, actions, readonly, setStage)}</div>
                    </Fragment>
                );
            }
            case "carousel":
                return (
                    <div className="line templateline">
                        {getElements(actions.evaluate("//actions/template/element", actions, null, XPathResult.ANY_TYPE, null), actions, readonly, setStage)}
                    </div>
                );
            default:
                break;
        }
    }
    return null;
}

export const mergeConvs = (conversations: conversation[], newconv: conversation): conversation[] => {
    const ret = [...conversations];
    const ind = ret.findIndex(sconv => sconv.cuid === newconv.cuid);
    if (ind === -1) {
        ret.push(newconv);
    } else {
        ret[ind] = mergeConv(ret[ind], newconv);
    }
    return ret;
};

export const mergeConv = (conv: conversation, newconv: conversation): conversation => {
    return { ...newconv, messages: mergeMessages(conv.messages, newconv.messages) };
};

export const mergeMessages = (messages: message[], newmessages: message[]): message[] => {
    const ret = [...messages];
    if (newmessages) {
        newmessages.forEach(msg => {
            if (ret.findIndex(smsg => smsg.muid === msg.muid) === -1) ret.push(msg);
        });
        ret.sort((a, b) => (a.created < b.created ? -1 : a.created > b.created ? 1 : 0));
    }
    return ret;
};

export default Log;
